import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { SERVICES } from "@/data/services";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FIXED_CATEGORIES = ["lectura", "hechiceria", "pacto"];

const bi = (es, en) => ({ es: es || en || "", en: en || es || "" });

const overlayModalities = (svc, dbSvc) => {
  if (!svc.modalities?.length) return svc.modalities;
  const extra = dbSvc.extra_prices || {};
  return svc.modalities.map((m) => {
    if (extra[m.id]) return { ...m, priceMXN: Number(extra[m.id].mxn ?? m.priceMXN), priceUSD: Number(extra[m.id].usd ?? m.priceUSD) };
    const isBase = m.priceMXN === svc.priceMXN && m.priceUSD === svc.priceUSD;
    return isBase ? { ...m, priceMXN: dbSvc.price_mxn, priceUSD: dbSvc.price_usd } : m;
  });
};

const withDiscount = (dbSvc) => (dbSvc.discount_percent ? {
  discountPercent: dbSvc.discount_percent,
  discountUntil: dbSvc.discount_until,
  discountLabel: dbSvc.discount_label,
  originalPriceMXN: dbSvc.original_price_mxn,
  originalPriceUSD: dbSvc.original_price_usd,
} : {});

const mergeStatic = (svc, dbSvc) => ({
  ...svc,
  hidden: !!dbSvc.hidden,
  category: dbSvc.category || svc.category,
  name: bi(dbSvc.name_es || svc.name.es, dbSvc.name_en || svc.name.en),
  description: dbSvc.description ? bi(dbSvc.description, dbSvc.description) : svc.description,
  priceMXN: Number(dbSvc.price_mxn),
  priceUSD: Number(dbSvc.price_usd),
  modalities: overlayModalities(svc, dbSvc),
  order: dbSvc.order || 0,
  ...withDiscount(dbSvc),
});

const fromDb = (dbSvc, catName) => ({
  id: dbSvc.id,
  category: dbSvc.category || "otro",
  name: bi(dbSvc.name_es, dbSvc.name_en),
  subtitle: bi(catName.es, catName.en),
  duration: bi("", ""),
  priceMXN: Number(dbSvc.price_mxn),
  priceUSD: Number(dbSvc.price_usd),
  modalities: [],
  badge: null,
  features: { es: [], en: [] },
  description: bi(dbSvc.description || "", dbSvc.description || ""),
  hidden: !!dbSvc.hidden,
  order: dbSvc.order || 0,
  fromDb: true,
  ...withDiscount(dbSvc),
});

const stripPrices = (svc) => ({
  ...svc,
  priceMXN: null,
  priceUSD: null,
  originalPriceMXN: undefined,
  originalPriceUSD: undefined,
  modalities: (svc.modalities || []).map((m) => ({ ...m, priceMXN: null, priceUSD: null })),
});

export const mergeCatalog = (dbServices = [], dbCategories = [], pricesVisible = true) => {
  const byId = Object.fromEntries(dbServices.map((s) => [s.id, s]));
  const catNames = Object.fromEntries(dbCategories.map((c) => [c.id, bi(c.name, c.name_en)]));
  const merged = SERVICES.map((s) => (byId[s.id] ? mergeStatic(s, byId[s.id]) : s));
  const staticIds = new Set(SERVICES.map((s) => s.id));
  dbServices.filter((s) => !staticIds.has(s.id)).forEach((s) => merged.push(fromDb(s, catNames[s.category] || bi(s.category, s.category))));
  return pricesVisible ? merged : merged.map(stripPrices);
};

export const useCatalog = () => {
  const { user, loading: authLoading } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["public-catalog", user?.id || user?._id || "anon"],
    queryFn: async () => {
      const token = localStorage.getItem("ca_token");
      return (await axios.get(`${API}/catalog/public`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })).data;
    },
    enabled: !authLoading,
    staleTime: 60_000,
    retry: 1,
  });
  const pricesVisible = !!user && (data ? data.prices_visible !== false : true);
  const services = useMemo(() => mergeCatalog(data?.services, data?.categories, pricesVisible), [data, pricesVisible]);
  const categories = useMemo(() => (data?.categories || []).map((c) => ({ ...c, label: bi(c.name, c.name_en) })), [data]);
  const visible = useMemo(() => services.filter((s) => !s.hidden), [services]);
  const extraCategories = useMemo(
    () => categories.filter((c) => !FIXED_CATEGORIES.includes(c.id) && visible.some((s) => s.category === c.id)),
    [categories, visible],
  );
  return { services, visible, categories, extraCategories, pricesVisible, loading: isLoading };
};
